import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, ChangeDetectorRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { TreeNodeSelectEvent, Tree } from 'primeng/tree';
import { ThesaurusTreeNode } from 'src/app/models/thesaurus-tree-node';
import { SearchFieldDto, ThesaurusSearchType } from 'src/app/models/search-field-dto';

@Component({
  selector: 'app-thesaurus-search-tree',
  templateUrl: './thesaurus-search-tree.component.html',
  styleUrls: ['./thesaurus-search-tree.component.scss']
})
export class ThesaurusSearchTreeComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() tree!: ThesaurusTreeNode;
  @Output() onToggle = new EventEmitter<ThesaurusTreeNode>();
  @Input() currentLanguage!: string;
  @Input() enableVirtualScroll: boolean = true;
  @Input() tempSelections: SearchFieldDto[] = [];
  @Input() showHoverLabel: boolean = false;
  @Input() searchTypePreferences: { [nodeId: string]: ThesaurusSearchType } = {};
  @Output() searchTypeChange = new EventEmitter<{node: ThesaurusTreeNode, searchType: ThesaurusSearchType}>();

  @ViewChild('searchTypeSelector') searchTypeSelector!: any;
  @ViewChild('treeComponent') treeComponent!: Tree;

  currentNode: ThesaurusTreeNode | null = null;
  expandedKeys: any;
  selectedNode: ThesaurusTreeNode | null = null;
  currentFilterValue: string = '';
  private matchingWordsTimeout: any;
  private isProcessingMatchingWords: boolean = false;
  private _suppressFocusScroll = false;
  private _originalProtoFocus: ((options?: FocusOptions) => void) | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    // When tree input changes, reapply matching words if filter is active
    if (changes['tree'] && !changes['tree'].firstChange && this.currentFilterValue) {
      // Use same debounce time as input filtering
      if (this.matchingWordsTimeout) {
        clearTimeout(this.matchingWordsTimeout);
      }
      this.matchingWordsTimeout = setTimeout(() => {
        this.updateMatchingWords(this.tree, this.currentFilterValue);
        this.cdr.detectChanges();
      }, 500);
    }

    // When language changes, refresh matching word labels if filter is active
    if (changes['currentLanguage'] && !changes['currentLanguage'].firstChange && this.currentFilterValue) {
      if (this.matchingWordsTimeout) {
        clearTimeout(this.matchingWordsTimeout);
      }
      this.matchingWordsTimeout = setTimeout(() => {
        this.updateMatchingWords(this.tree, this.currentFilterValue);
        this.cdr.detectChanges();
      }, 50);
    }
  }

  ngAfterViewInit() {
    if (this.enableVirtualScroll && this.treeComponent) {
      const tree = this.treeComponent as any;

      // Patch HTMLElement.prototype.focus to use preventScroll:true while a toggle is in progress.
      // PrimeNG UITreeNode.focusVirtualNode() calls element.focus() 1ms after expand/collapse,
      // which triggers browser scrollIntoView and causes a visible jump.
      // We patch the native prototype to guarantee interception regardless of bundle chunking.
      if (!this._originalProtoFocus) {
        this._originalProtoFocus = HTMLElement.prototype.focus;
        const self = this;
        HTMLElement.prototype.focus = function(this: HTMLElement, options?: FocusOptions) {
          self._originalProtoFocus!.call(
            this,
            self._suppressFocusScroll ? { ...(options ?? {}), preventScroll: true } : options
          );
        };
      }

      // Patch updateSerializedValue to keep the viewport anchored when a node above
      // the visible area is expanded/collapsed (inserted/removed items shift indices).
      const originalUpdateFn = tree.updateSerializedValue?.bind(tree);
      if (originalUpdateFn) {
        const self = this;
        tree.updateSerializedValue = () => {
          const scroller = tree.scroller as any;
          // PrimeNG Scroller exposes its scroll container as elementViewChild (not 'el')
          const scrollEl = scroller?.elementViewChild?.nativeElement;
          if (!scrollEl || !(tree.serializedValue as any[])?.length) {
            originalUpdateFn();
            return;
          }
          const itemSize: number = (typeof scroller?._itemSize === 'number') ? scroller._itemSize : 40;
          const scrollTopBefore: number = scrollEl.scrollTop;
          const firstVisibleIndex = Math.floor(scrollTopBefore / itemSize);
          // Anchor on the TreeNode object reference for reliable identity
          const anchorNodeRef = (tree.serializedValue as any[])[firstVisibleIndex]?.node;

          // Suppress focus-induced scroll for the duration of focusVirtualNode (fires at +1ms)
          self._suppressFocusScroll = true;
          setTimeout(() => { self._suppressFocusScroll = false; }, 20);

          originalUpdateFn(); // rebuilds serializedValue synchronously

          // Determine the target scroll position after the toggle
          let targetScrollTop = scrollTopBefore;
          if (anchorNodeRef && (tree.serializedValue as any[])?.length) {
            const newIndex = (tree.serializedValue as any[]).findIndex((sv: any) => sv.node === anchorNodeRef);
            if (newIndex !== -1 && newIndex !== firstVisibleIndex) {
              // Anchor shifted (node expanded/collapsed above the viewport): correct position
              targetScrollTop = newIndex * itemSize;
            }
          }

          // Apply scroll correction synchronously (covers above-viewport anchor shifts)
          const applyScrollTarget = (top: number) => {
            scrollEl.scrollTop = top;
            scroller.first = Math.floor(top / itemSize);
            if (typeof scroller.setContentPosition === 'function') {
              scroller.setContentPosition(null);
            }
            if (typeof scroller.lastScrollPos === 'number') {
              scroller.lastScrollPos = top;
            }
          };

          if (targetScrollTop !== scrollTopBefore) {
            applyScrollTarget(targetScrollTop);
          }

          // Restore scroll position after any async operations (CD, focus) that may jump the view.
          // focusVirtualNode fires at +1 ms; this correction runs at +10 ms to win the race.
          const finalTarget = targetScrollTop;
          setTimeout(() => {
            if (Math.abs(scrollEl.scrollTop - finalTarget) > 2) {
              applyScrollTarget(finalTarget);
            }
          }, 10);
        };
      }
    }

    if (this.treeComponent && this.treeComponent.filterViewChild) {
      const inputElement = this.treeComponent.filterViewChild.nativeElement;
      inputElement.addEventListener('input', () => {
        // Clear previous timeout
        if (this.matchingWordsTimeout) {
          clearTimeout(this.matchingWordsTimeout);
        }

        const filterValue = inputElement.value?.toLowerCase() || '';

        // If filter is empty, update immediately
        if (!filterValue) {
          this.currentFilterValue = '';
          this.clearMatchingWords(this.tree);
          this.cdr.detectChanges();
        } else {
          // Use longer debounce for better performance with large trees
          const debounceTime = 500;

          // Debounce the updateMatchingWords call for non-empty filters
          this.matchingWordsTimeout = setTimeout(() => {
            if (this.isProcessingMatchingWords) return;

            this.currentFilterValue = filterValue;
            this.isProcessingMatchingWords = true;

            // Update matching words
            this.updateMatchingWords(this.tree, filterValue);

            // Force virtual scroller to re-render with updated matching words
            if (this.treeComponent && this.enableVirtualScroll) {
              // Trigger virtual scroller update by re-applying filter
              const currentFilter = this.treeComponent.filterViewChild?.nativeElement.value;
              if (currentFilter) {
                // Small delay to ensure matching words are processed
                setTimeout(() => {
                  this.treeComponent._filter(currentFilter);
                  this.cdr.detectChanges();
                }, 10);
              }
            } else {
              this.cdr.detectChanges();
            }

            this.isProcessingMatchingWords = false;
          }, debounceTime);
        }
      });
    }
  }

  // Custom filter function for PrimeNG tree
  customTreeFilter = (value: string, filter: string): boolean => {
    if (!filter || filter.trim() === '') {
      return true;
    }

    const filterLower = filter.toLowerCase().trim();
    const valueLower = (value || '').toLowerCase();

    return valueLower.includes(filterLower);
  }

  // Quickly clear all matching words without recursive search
  private clearMatchingWords(node: ThesaurusTreeNode | undefined): void {
    if (!node) return;
    node.matchingWord = undefined;
    node.ariaLabel = undefined;
    node.label = this.getBaseLabel(node);
    if (node.children?.length) {
      for (let i = 0; i < node.children.length; i++) {
        this.clearMatchingWords(node.children[i]);
      }
    }
  }

  private getBaseLabel(node: ThesaurusTreeNode): string {
    const translatedLabel = node.multiLangLabel
      ? (this.currentLanguage === 'he' ? node.multiLangLabel.hebrew : node.multiLangLabel.english)
      : '';

    if (translatedLabel) {
      return translatedLabel;
    }

    if (!node.originalLabel) {
      node.originalLabel = node.label || '';
    }

    return node.originalLabel || node.label || '';
  }

  // Recursively update nodes with matching word information
  private updateMatchingWords(node: ThesaurusTreeNode | undefined, filter: string, depth: number = 0): void {
    if (!node) return;

    if (!filter) {
      node.matchingWord = undefined;
      node.ariaLabel = undefined;
      node.label = this.getBaseLabel(node);
      // Recursively clear children
      if (node.children?.length) {
        for (let i = 0; i < node.children.length; i++) {
          this.clearMatchingWords(node.children[i]);
        }
      }
      return;
    }

    const filterLower = filter.toLowerCase();

    // Check if main label matches first (most common case)
    const mainLabel = this.getBaseLabel(node);
    const mainLabelLower = mainLabel.toLowerCase();

    if (mainLabelLower.includes(filterLower)) {
      // Main label matches, no need to show matching word
      node.matchingWord = undefined;
      node.ariaLabel = undefined;
      node.label = mainLabel;
    } else {
      // Only check equal/unpref words if they exist (skip if no alternate words)
      let foundMatch = false;

      if (node.equalWids?.length) {
        for (let i = 0; i < node.equalWids.length; i++) {
          const equalWord = node.equalWids[i];
          const equalText = this.currentLanguage === 'he' ? equalWord.name.hebrew : equalWord.name.english;
          if (equalText.toLowerCase().includes(filterLower)) {
            node.matchingWord = { text: equalText, type: 'equal' };
            const equalTypeLabel = this.currentLanguage === 'he' ? 'מילים שוות ערך' : 'Equal words';
            node.ariaLabel = `${mainLabel}. ${equalTypeLabel}: ${equalText}`;
            node.label = node.ariaLabel;
            foundMatch = true;
            break;
          }
        }
      }

      // Check unpref words only if no equal word matched
      if (!foundMatch && node.unprefWids?.length) {
        for (let i = 0; i < node.unprefWids.length; i++) {
          const unprefWord = node.unprefWids[i];
          const unprefText = this.currentLanguage === 'he' ? unprefWord.name.hebrew : unprefWord.name.english;
          if (unprefText.toLowerCase().includes(filterLower)) {
            node.matchingWord = { text: unprefText, type: 'unpref' };
            const unprefTypeLabel = this.currentLanguage === 'he' ? 'מילים לא מועדפות' : 'Unpreferred words';
            node.ariaLabel = `${mainLabel}. ${unprefTypeLabel}: ${unprefText}`;
            node.label = node.ariaLabel;
            foundMatch = true;
            break;
          }
        }
      }

      if (!foundMatch) {
        node.matchingWord = undefined;
        node.ariaLabel = undefined;
        node.label = mainLabel;
      }
    }

    // Process children recursively if they exist
    if (node.children?.length) {
      for (let i = 0; i < node.children.length; i++) {
        this.updateMatchingWords(node.children[i], filter, depth + 1);
      }
    }
  }

  onNodeClick(event: MouseEvent, node: ThesaurusTreeNode) {
    // Only handle real mouse clicks (detail > 0), not keyboard-triggered clicks
    if (!(event instanceof MouseEvent) || event.detail === 0) {
      return;
    }

    // Don't trigger on search type icon clicks
    if ((event.target as HTMLElement).closest('.node-search-type-icon')) {
      event.stopPropagation();
      return;
    }

    // Stop propagation for all custom handling
    event.stopPropagation();

    // Check if this is the all-categories root or a category header
    const isAllCategoriesRoot = node.styleClass === 'all-categories-root';
    const isCategoryHeader = node.styleClass === 'category-header-node' && node.categoryId !== undefined;

    // For all-categories root or category headers, toggle expansion
    if (isAllCategoriesRoot || isCategoryHeader) {
      this.toggleCategoryExpansion(node);
      return;
    }

    // For regular nodes, trigger selection
    this.handleToggle(node);
  }

  handleSelection(selectEvent: TreeNodeSelectEvent) {
    const event = selectEvent.originalEvent;
    const node = selectEvent.node as ThesaurusTreeNode;

    // Check if this is the all-categories root or a category header
    const isAllCategoriesRoot = node.styleClass === 'all-categories-root';
    const isCategoryHeader = node.styleClass === 'category-header-node' && node.categoryId !== undefined;

    // If mouse event, it was handled by onNodeClick
    if (event instanceof MouseEvent && event.detail > 0) {
      if (isAllCategoriesRoot || isCategoryHeader) {
        setTimeout(() => {
          this.selectedNode = null;
        }, 0);
      }
      return;
    }

    // Handle keyboard events (Enter/Space)
    if (event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      
      if (isAllCategoriesRoot || isCategoryHeader) {
        this.toggleCategoryExpansion(node);
        return;
      }
      
      // For regular nodes, trigger selection
      this.handleToggle(node);
    }
  }

  private toggleCategoryExpansion(node: ThesaurusTreeNode) {
    const nodeKey = node.key || node.data?.toString() || '';
    if (!nodeKey) return;

    if (!this.expandedKeys) {
      this.expandedKeys = {};
    }
    
    const isExpanding = !this.expandedKeys[nodeKey];
    
    // Toggle expansion
    if (this.expandedKeys[nodeKey]) {
      delete this.expandedKeys[nodeKey];
    } else {
      this.expandedKeys[nodeKey] = true;
    }
    
    this.expandedKeys = {...this.expandedKeys};
    this.cdr.detectChanges();
    
    // If expanding, select first child for keyboard navigation
    if (isExpanding && node.children && node.children.length > 0) {
      setTimeout(() => {
        this.selectedNode = node.children![0];
        this.cdr.detectChanges();
      }, 100);
    } else {
      setTimeout(() => {
        this.selectedNode = null;
      }, 0);
    }
  }

  handleToggle(node: ThesaurusTreeNode) {
    this.onToggle.emit(node);
  }

  isNodeSelected(node: ThesaurusTreeNode): boolean {
    const id = node.data?.toString();
    return !!this.tempSelections.find(s => s.id === id);
  }

  getNodeSearchType(node: ThesaurusTreeNode): ThesaurusSearchType {
    const nodeId = node.key || node.data?.toString();

    // First check if there's a preference set for this node
    if (nodeId && this.searchTypePreferences[nodeId]) {
      return this.searchTypePreferences[nodeId];
    }

    // Match by composite key ID
    const selection = this.tempSelections.find(s => s.id === nodeId);
    if (selection?.searchType) {
      return selection.searchType;
    }

    // Default to branch
    return 'branch';
  }

  getSearchTypeIcon(node: ThesaurusTreeNode): string {
    const searchType = this.getNodeSearchType(node);
    const icons = {
      'branch': 'assets/branch.svg',
      'term': 'assets/mid.svg',
      'word': 'assets/wid.svg'
    };
    return icons[searchType];
  }

  openSearchTypePopup(event: Event, node: ThesaurusTreeNode) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.currentNode = node;
    const target = event.target as HTMLElement;
    // Explicitly set the value before toggling
    this.searchTypeSelector.value = this.getNodeSearchType(node);
    // Explicitly set the opener element
    this.searchTypeSelector.openerElement = target;
    this.searchTypeSelector.toggle(event);
  }

  handleSearchTypeIconKeydown(event: KeyboardEvent, node: ThesaurusTreeNode) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openSearchTypePopup(event, node);
    }
  }

  onSearchTypeChange(searchType: ThesaurusSearchType) {
    if (this.currentNode) {
      this.searchTypeChange.emit({ node: this.currentNode, searchType });
    }
  }

  getCurrentNodeId(): string {
    return this.currentNode?.data?.toString() || 'default';
  }

  clearFilter() {
    if (this.treeComponent && this.treeComponent.filterViewChild) {
      this.treeComponent.filterViewChild.nativeElement.value = '';
      this.treeComponent._filter('');
    }
  }

  ngOnDestroy() {
    if (this.matchingWordsTimeout) {
      clearTimeout(this.matchingWordsTimeout);
    }
    // Restore original HTMLElement.prototype.focus
    if (this._originalProtoFocus) {
      HTMLElement.prototype.focus = this._originalProtoFocus;
      this._originalProtoFocus = null;
    }
  }
}
